import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AccountAdapter } from '../../adapters/account.adapter';
import {
  deactivateCompanyAccount,
  deactivateCompanyAccountFailure,
  deactivateCompanyAccountSuccess,
  loadCompanyAccount,
  loadCompanyAccountFailure,
  loadCompanyAccountSuccess,
  loadCompanyPrivacy,
  loadCompanyPrivacySuccess,
  updateCompanyAccount,
  updateCompanyAccountFailure,
  updateCompanyAccountSuccess,
  updateCompanyPrivacy,
  updateCompanyPrivacyFailure,
  updateCompanyPrivacySuccess,
} from './company-account.actions';
import { logout } from '../auth/auth.actions';
import { selectCompanyAccount } from './company-account.reducer';

@Injectable()
export class CompanyAccountEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly companyAccountAdapter = inject(AccountAdapter);

  loadCompanyAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCompanyAccount),
      withLatestFrom(this.store.select(selectCompanyAccount)),
      filter(([, account]) => account == null),
      exhaustMap(() =>
        this.companyAccountAdapter.getAccount().pipe(
          map((account) => loadCompanyAccountSuccess({ account })),
          catchError((error) => {
            return of(loadCompanyAccountFailure({ error }));
          }),
        ),
      ),
    ),
  );

  updateCompanyAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCompanyAccount),
      mergeMap((props) =>
        this.companyAccountAdapter.editAccount(props.account).pipe(
          map(() => updateCompanyAccountSuccess({ account: props.account })),
          catchError((error) => {
            return of(updateCompanyAccountFailure({ error }));
          })
        )
      )
    )
  );

  loadCompanyAccountFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadCompanyAccountFailure),
        tap((action) => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );

  loadCompanyPrivacy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCompanyPrivacy),
      exhaustMap(() =>
        this.companyAccountAdapter.getPrivacyInformation().pipe(
          map((privacy) => loadCompanyPrivacySuccess({ privacy })),
          catchError((error) => {
            return of(loadCompanyAccountFailure({ error }));
          })
        )
      )
    )
  );

  updateCompanyPrivacy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCompanyPrivacy),
      exhaustMap((props) =>
        this.companyAccountAdapter.editPrivacyInformation(props.privacy).pipe(
          map(() => updateCompanyPrivacySuccess({ privacy: props.privacy })),
          catchError((error) => {
            return of(updateCompanyPrivacyFailure({ error }));
          })
        )
      )
    )
  );

  deactivateCompanyAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deactivateCompanyAccount),
      exhaustMap((props) =>
        this.companyAccountAdapter.deactivateAccount().pipe(
          map(() => deactivateCompanyAccountSuccess()),
          catchError((error) => {
            return of(deactivateCompanyAccountFailure({ error }));
          })
        )
      )
    )
  );

  deactivateCompanyAccountSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deactivateCompanyAccountSuccess),
        map(() => logout())
      ),
    { dispatch: false }
  );
}
